
import { useSelector } from "react-redux";
import RejectionReasonModal from "./components/RejectionReasonModal";
import MediaStatusSection from "./components/MediaStatusSection";
import ConfirmationModal from "../../../../../components/ConfirmationModal";
import { MediaItem } from "../../Media.types";
import { RootState } from "../../../../../store";
import { formatAddress, formatDate } from "../../../../../utils/helper";
import { REJECTION_REASONS_OPTIONS, STATUS_CONFIG } from "../../constants";

interface Props {
  item: MediaItem;
  onClose: () => void;
  pendingAction: "VERIFY" | "REJECT" | null;
  setPendingAction: React.Dispatch<
    React.SetStateAction<"VERIFY" | "REJECT" | null>
  >;
  handleConfirmAction: () => Promise<void>;
  showRejectModal: boolean;
  setShowRejectModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRejectReason: any;
  setSelectedRejectReason: React.Dispatch<React.SetStateAction<any>>;
}

const MediaPreviewModal: React.FC<Props> = ({
  item,
  onClose,
  pendingAction,
  setPendingAction,
  handleConfirmAction,
  showRejectModal,
  setShowRejectModal,
  selectedRejectReason,
  setSelectedRejectReason,
}) => {
  const { label, icon: Icon, badgeClass } = STATUS_CONFIG[item.status];

  const categoriesByCode = useSelector((state: RootState) =>
      item?.storeInfo?.category
        ? state.reference.categoriesByCode[item.storeInfo.category]?.name
        : undefined,
    );
  
    const subcategoriesByCode = useSelector((state: RootState) =>
      item?.storeInfo?.subCategory
        ? state.reference.subcategoriesByCode[item.storeInfo.subCategory]?.name
        : undefined,
    );

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      width: "100%",
      padding: "0.6rem",
      border: "1px solid #d4d4d8", // Tailwind's border-neutral-300
      borderRadius: "0.75rem",
    }),
    option: (provided: any) => ({
      ...provided,
      color: "#4b5563", // Tailwind's neutral-400
      fontSize: "0.875rem",
    }),
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 bg-white w-[90%] flex flex-col items-center rounded-2xl  max-w-5xl h-[80vh]  overflow-hidden">
        <div className="h-[10%] flex justify-between items-center w-full px-10  ">
          <span className="text-2xl font-semibold">{item?.name}</span>
          <button
            onClick={onClose}
            className="  text-gray-500 hover:text-black"
          >
            ✕
          </button>
        </div>
        <div className="w-full h-[90%]  flex">
          {/* Left: Media */}
          <div className="w-1/2 bg-gray-100 flex items-center justify-center">
            {item?.type === "IMAGE" ? (
              <img
                src={item.mediaUrl}
                alt={item?.storeInfo?.storeName}
                className="max-h-full object-contain"
              />
            ) : (
              <video controls className="max-h-full object-contain">
                <source src={item?.mediaUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>

          {/* Right: Details */}
          <div className="w-1/2 p-6 flex flex-col gap-2">
            <div className=" flex justify-between ">
              <h2 className="text-xl font-semibold">
                {item?.storeInfo?.storeName || "Unnamed Store"}
              </h2>
              <span className={badgeClass}>
                {Icon && <Icon fontSize="small" />} {label}
              </span>
            </div>
            <p className="text-sm text-gray-500">{`${subcategoriesByCode}, ${categoriesByCode}`}</p>

            <div className="rounded-lg flex-1 flex flex-col gap-2">
              <div className="flex flex-col bg-slate-50 border-[1px] border-gray-200 pl-4 flex-1 rounded-xl justify-center ">

                <strong>Location</strong>{" "}
                {formatAddress(item?.storeInfo?.address)}

              </div>
              <div className="flex flex-col bg-slate-50 border-[1px] border-gray-200 pl-4 flex-1 rounded-xl justify-center ">
                    <strong>Created on</strong> 
                    <span>{item?.createdAt && formatDate(item?.createdAt)}</span>
              </div>

              <div className="flex flex-col bg-slate-50 border-[1px] border-gray-200 px-4 flex-1 rounded-xl justify-center">

                <strong>Media specs</strong> 
                <div className="flex gap-1 justify-between text-sm">
                  <span className="flex-1 bg-blue-50 p-2 rounded-lg"><strong>Type :</strong> {item?.type.charAt(0).toUpperCase() + item?.type.slice(1).toLowerCase()}</span>
                  <span className="flex-1 bg-blue-50 p-2 rounded-lg"><strong>Duration :</strong> {item?.length}s</span>
                </div>

              </div>
            </div>

            <div className="pt-4">
              <div className="pt-4">
                <MediaStatusSection
                  status={item.status}
                  setPendingAction={setPendingAction}
                  setShowRejectModal={setShowRejectModal}
                  published = {item?.published}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <RejectionReasonModal
        isOpen={showRejectModal}
        selectedReason={selectedRejectReason}
        onChange={(val) => setSelectedRejectReason(val)}
        onClose={() => setShowRejectModal(false)}
        onConfirm={() => {
          setPendingAction("REJECT");
          setShowRejectModal(false);
        }}
        options={REJECTION_REASONS_OPTIONS}
        styles={customStyles}
      />

      <ConfirmationModal
        isOpen={!!pendingAction}
        title={pendingAction === "VERIFY" ? "Verify Media" : "Reject Media"}
        description={
          pendingAction === "VERIFY"
            ? "Are you sure you want to verify this media?"
            : "Are you sure you want to reject this media?"
        }
        confirmText={pendingAction === "VERIFY" ? "Verify" : "Reject"}
        variant={pendingAction === "REJECT" ? "danger" : "success"}
        onConfirm={handleConfirmAction}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
};

export default MediaPreviewModal;
